<template>
  <div class="puzzle-screen">

    <!-- HEADER CARD -->
    <div class="puzzle-card">
      <div class="puzzle-header-title">{{ translation.tryThePuzzle }}</div>

      <div class="puzzle-hint">{{ translation.hint }}</div>

      <!-- MOVES BAR -->
      <div class="moves-bar">
        <div class="moves-track">
          <div class="moves-fill"
            :style="{ width: Math.min((count / 20) * 100, 100) + '%', background: count > 15 ? '#ff6b6b' : '#0090e3' }">
          </div>
        </div>
        <div class="moves-label">
          <span class="moves-num" :style="{ color: count > 15 ? '#ff6b6b' : 'white' }">{{ count }}</span>
          <span class="moves-max">/ 20</span>
          <span class="moves-text">{{ translation.numberOfTry }}</span>
        </div>
      </div>
    </div>

    <!-- PUZZLE GRID -->
    <div v-if="puzzleImageSrc" :class="['puzzle-board', { 'puzzle-solved': solved }]">
      <template v-if="solved">
        <!-- Image entière affichée 3 sec après la victoire -->
        <div class="puzzle-complete-img" :style="{ backgroundImage: 'url(' + puzzleImageSrc + ')' }"></div>
      </template>
      <template v-else>
        <div v-for="(piece, cellIndex) in pieces" :key="cellIndex" class="swap-piece"
          :class="{ selected: selected === cellIndex, correct: piece === cellIndex }" :style="{
            backgroundImage: 'url(' + puzzleImageSrc + ')',
            backgroundSize: '300% 300%',
            backgroundPosition: bgPos(piece)
          }" @click="selectPiece(cellIndex)">
          <div v-if="selected === cellIndex" class="piece-highlight"></div>
        </div>
      </template>
    </div>
    <div v-else class="puzzle-no-img">🖼️</div>

    <!-- SKIP BUTTON -->
    <button @click="skipPuzzle" class="skip-btn">
      ← {{ translation.skipPuzzle }}
    </button>

    <!-- SUCCESS OVERLAY -->
    <div v-if="completed" class="result-overlay">
      <div class="result-card success">
        <div class="result-confetti">
          <span v-for="n in 12" :key="n" class="confetti-dot" :style="{ '--i': n }"></span>
        </div>
        <div class="result-icon">🎉</div>
        <div class="result-title">{{ translation.bravo }}</div>
        <div class="result-score">+{{ Math.max(0, 20 - count) }} pts</div>
      </div>
    </div>

    <!-- FAILED OVERLAY -->
    <div v-if="nextTime" class="result-overlay">
      <div class="result-card failed">
        <div class="result-icon">💪</div>
        <div class="result-title">{{ translation.nextTime }}</div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  props: ['pointData'],
  data() {
    return {
      pieces: [],
      selected: null,
      count: 0,
      solved: false,
      completed: false,
      nextTime: false,
      audio: new Audio('/sounds/correctAnswer.mp3'),
    };
  },

  watch: {
    pieces(newPieces) {
      if (newPieces.length === 9 && newPieces.every((v, i) => v === i) && !this.completed && !this.nextTime) {
        this.onVictory();
      }
    },
  },

  computed: {
    puzzleImageSrc() {
      try {
        const p = this.pointData;
        if (!p) return '';
        let src = localStorage.getItem('puzzleImageBase64') || '';
        if (!src) {
          src = p.puzzleImageBase64 || p.imageBase64 || p.puzzleImageUrl || p.imageUrl || '';
          if (src && src.startsWith('data:')) localStorage.setItem('puzzleImageBase64', src);
        }
        if (!src) return '';
        if (src.startsWith('http') || src.startsWith('data:image')) return src;
        return `data:image/png;base64,${src}`;
      } catch { return ''; }
    },

    translation() {
      const lang = this.$store.getters.lang || 'he';
      const lib = {
        ar: {
          nextTime: 'تقريباً! حاولوا مرة أخرى في النقطة التالية',
          tryThePuzzle: 'رتبوا القطع لإعادة تركيب الصورة',
          skipPuzzle: 'تخطى البازل',
          numberOfTry: 'تبديلات',
          bravo: '!أحسنتم',
          hint: 'اضغط على قطعة ثم على قطعة أخرى لتبديلهما',
        },
        en: {
          nextTime: 'Almost! Do better next time!',
          tryThePuzzle: 'Rearrange the pieces to rebuild the image',
          skipPuzzle: 'Skip puzzle',
          numberOfTry: 'swaps',
          bravo: 'Well done!',
          hint: 'Tap a piece, then tap another to swap them',
        },
        he: {
          nextTime: 'כמעט! תעשו יותר טוב בנקודה הבאה!',
          tryThePuzzle: 'סדרו את החלקים כדי לשחזר את התמונה',
          skipPuzzle: 'לדלג על הפאזל',
          numberOfTry: 'החלפות',
          bravo: '!כל הכבוד',
          hint: 'לחצו על חלק ואז על חלק אחר להחלפה',
        },
        fr: {
          nextTime: 'Presque ! Faites mieux au prochain point !',
          tryThePuzzle: 'Réorganisez les pièces pour reconstituer l\'image',
          skipPuzzle: 'Passer le puzzle',
          numberOfTry: 'échanges',
          bravo: 'Bravo !',
          hint: 'Touchez une pièce puis une autre pour les échanger',
        },
      };
      return lib[lang] || lib.he;
    },
  },

  mounted() {
    this.initPuzzle();
  },

  methods: {
    initPuzzle() {
      const arr = [0, 1, 2, 3, 4, 5, 6, 7, 8];
      // Apply exactly 10 random swaps so the puzzle is always solvable in ~10 moves
      for (let k = 0; k < 10; k++) {
        const i = Math.floor(Math.random() * 9);
        let j;
        do { j = Math.floor(Math.random() * 9); } while (j === i);
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      if (this.isSolved(arr)) return this.initPuzzle();
      this.pieces = arr;
    },

    isSolved(arr) {
      return arr.every((v, i) => v === i);
    },

    bgPos(pieceIndex) {
      const col = pieceIndex % 3;
      const row = Math.floor(pieceIndex / 3);
      return `${col * 50}% ${row * 50}%`;
    },

    selectPiece(cellIndex) {
      if (this.completed || this.nextTime) return;

      if (this.selected === null) {
        this.selected = cellIndex;
      } else {
        if (this.selected === cellIndex) {
          this.selected = null;
          return;
        }
        // Swap the two pieces
        const arr = [...this.pieces];
        [arr[this.selected], arr[cellIndex]] = [arr[cellIndex], arr[this.selected]];
        this.pieces = arr;
        this.count++;
        this.selected = null;

        if (this.count >= 20 && !this.completed) {
          this.nextTime = true;
          setTimeout(() => { this.finishPoint(); }, 2000);
        }
      }
    },

    onVictory() {
      const score = Math.max(0, 20 - this.count);
      this.$emit('star-up', score);
      this.solved = true;
      setTimeout(() => {
        this.completed = true;
        setTimeout(() => { this.finishPoint(); }, 2000);
      }, 3000);
    },

    finishPoint() {
      this.audio.play().catch(() => { });
      localStorage.removeItem('puzzleImageBase64');
      setTimeout(() => {
        this.$emit('puzzle-done');
      }, 400);
    },

    skipPuzzle() {
      localStorage.removeItem('puzzleImageBase64');
      this.$emit('puzzle-done');
    },
  },
};
</script>

<style scoped>
.puzzle-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 16px 16px 24px;
  box-sizing: border-box;
}

/* ── HEADER CARD ── */
.puzzle-card {
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  border: 1.5px solid rgba(255, 255, 255, 0.35);
  border-radius: 22px;
  padding: 18px 24px;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 4px 24px rgba(30, 100, 200, 0.15);
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: center;
}

.puzzle-header-title {
  font-size: 18px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.85);
  text-align: center;
}

/* ── MOVES BAR ── */
.moves-bar {
  width: 100%;
  max-width: 330px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.moves-track {
  width: 100%;
  height: 8px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 50px;
  overflow: hidden;
}

.moves-fill {
  height: 100%;
  border-radius: 50px;
  transition: width 0.3s ease, background 0.3s ease;
}

.moves-label {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 4px;
}

.moves-num {
  font-size: 20px;
  font-weight: 900;
  transition: color 0.3s;
}

.moves-max {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.45);
}

.moves-text {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.55);
  margin-right: 4px;
}

/* ── PUZZLE GRID ── */
.puzzle-board {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  width: 330px;
  height: 330px;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.45), 0 0 0 3px rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
  direction: ltr;
}

.swap-piece {
  position: relative;
  width: 100%;
  height: 100%;
  cursor: pointer;
  transition: transform 0.15s, filter 0.15s;
  background-repeat: no-repeat;
}

.swap-piece:active {
  transform: scale(0.95);
}

.swap-piece.selected {
  filter: brightness(1.25);
  z-index: 2;
  box-shadow: inset 0 0 0 3px #ffd166;
}

.swap-piece.selected .piece-highlight {
  position: absolute;
  inset: 0;
  background: rgba(255, 209, 102, 0.25);
  pointer-events: none;
}

.puzzle-board.puzzle-solved {
  box-shadow: 0 0 0 5px #22c55e, 0 8px 32px rgba(34, 197, 94, 0.5);
  animation: solvedPulse 0.5s ease;
}

@keyframes solvedPulse {
  0% { box-shadow: 0 0 0 0px #22c55e; }
  50% { box-shadow: 0 0 0 12px rgba(34, 197, 94, 0.4); }
  100% { box-shadow: 0 0 0 5px #22c55e, 0 8px 32px rgba(34, 197, 94, 0.5); }
}

.puzzle-complete-img {
  grid-column: 1 / -1;
  grid-row: 1 / -1;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
}

.puzzle-no-img {
  width: 330px;
  height: 330px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 60px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 14px;
}

/* ── HINT ── */
.puzzle-hint {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.85);
  text-align: center;
  font-style: italic;
}

/* ── SKIP BUTTON ── */
.skip-btn {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 1);
  font-size: 16px;
  font-family: VarelaRound, sans-serif;
  cursor: pointer;
  padding: 4px 0;
  margin-top: 8px;
  -ms-flex-item-align: start;
  align-self: flex-start;
  direction: rtl;
  -webkit-transition: color 0.2s;
  transition: color 0.2s;
}

.skip-btn:active {
  color: white;
}

/* ── RESULT OVERLAYS ── */
.result-overlay {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  animation: overlayFadeIn 0.3s ease;
}

@keyframes overlayFadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.result-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 44px 56px;
  border-radius: 32px;
  text-align: center;
  animation: popIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  overflow: hidden;
}

.result-card.success {
  background: linear-gradient(145deg, #004f8a, #0090e3 60%, #00c2ff);
  box-shadow: 0 24px 80px rgba(0, 144, 227, 0.55), 0 0 0 2px rgba(255, 255, 255, 0.15);
}

.result-card.failed {
  background: linear-gradient(135deg, #1a1a2e, #2d2d5e);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

.result-icon {
  font-size: 68px;
  animation: iconBounce 0.6s 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

@keyframes iconBounce {
  from {
    transform: scale(0);
    opacity: 0;
  }

  to {
    transform: scale(1);
    opacity: 1;
  }
}

.result-title {
  font-size: 28px;
  font-weight: 900;
  color: white;
  font-family: VarelaRound, sans-serif;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.result-score {
  font-size: 42px;
  font-weight: 900;
  color: #ffd166;
  text-shadow: 0 0 20px rgba(255, 209, 102, 0.6);
  animation: scorePop 0.5s 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

@keyframes scorePop {
  from {
    transform: scale(0.4);
    opacity: 0;
  }

  to {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes popIn {
  from {
    transform: scale(0.5);
    opacity: 0;
  }

  to {
    transform: scale(1);
    opacity: 1;
  }
}

/* ── CONFETTI DOTS ── */
.result-confetti {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
}

.confetti-dot {
  position: absolute;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  top: calc(50% - 5px);
  left: calc(50% - 5px);
  background: hsl(calc(var(--i) * 30), 100%, 65%);
  animation: confettiFly 1.4s calc(var(--i) * 0.07s) ease-out both;
  transform-origin: 5px 5px;
}

@keyframes confettiFly {
  0% {
    transform: rotate(calc(var(--i) * 30deg)) translateY(0) scale(0);
    opacity: 1;
  }

  60% {
    opacity: 1;
  }

  100% {
    transform: rotate(calc(var(--i) * 30deg)) translateY(-130px) scale(1.4);
    opacity: 0;
  }
}

@font-face {
  font-family: VarelaRound;
  src: url(../../static/fonts/VarelaRound-Regular.ttf);
}
</style>
